import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { syncPeopleBuilderStatuses } from '../../utils/newPeopleBuilderSettingsUtils.js';
import {
    Button,
    Card,
    CardContent,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead, TableRow,
    Typography
} from '@mui/material';
import moment from 'moment';
import { useStoreState } from 'pullstate';
import { useEffect, useMemo, useState, useCallback } from 'react';
import BackdropLoader from '../../components/BackdropLoader.jsx';
import globalStyles from '../../components/styles/css/GlobalStyles.module.css';
import { AppStore } from '../../stores/AppStore.js';
import { deletePeopleBuilderSettingsAPI } from '../../utils/newPeopleBuilderSettingsUtils.js';
import NewPeopleBuilderSettings from './NewPeopleBuilderSettings.jsx';

const PeopleBuilderSettings = () => {
    const [openBackdrop, setOpenBackdrop] = useState(false)
    const [peopleBuilderSettings, setPeopleBuilderSettings] = useState([])
    const [peopleBuilderId, setPeopleBuilderId] = useState()
    const [editingRowId, setEditingRowId] = useState('')
    const [focusRow, setFocusRow] = useState(null)
    const [openPeopleBuilderForm, setOpenPeopleBuilderForm] = useState(false)
    const openAlert = useStoreState(AppStore, s => s.snackbar.openAlert);
    const { pbSettings } = useStoreState(AppStore);
    const months = useMemo(() => [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ])

    const [pbSettingsSelectedToDelete, setPbSettingsSelectedToDelete] = useState({
        start_date: '',
        id: ''
    })
    const [openDeletePbSettings, setOpenDeletePbSettings] = useState(false)

    const handleClose = () => {
        setOpenPeopleBuilderForm(false)
    }

    const deletePbSettings = useCallback(async () => {
        setOpenBackdrop(true)
        setOpenDeletePbSettings(false)
        const id = pbSettingsSelectedToDelete.id

        const result = await deletePeopleBuilderSettingsAPI(id, openAlert)
        if (result.success) {
            openAlert(result.data.message, 'success')

            const updatedSettings = peopleBuilderSettings.filter(peopleBuilder => peopleBuilder.id !== id)
            setPeopleBuilderSettings(updatedSettings)
            AppStore.update(s => {
                s.pbSettings = updatedSettings
            })
        }

        setOpenBackdrop(false)
    }, [pbSettingsSelectedToDelete, peopleBuilderSettings, openAlert])

    useEffect(() => {
        if (!pbSettings) return;
        const values = pbSettings.filter(value => value.status !== 'archived')
        setPeopleBuilderSettings(values)
    }, [pbSettings])

    return (
        <Container sx={{ mt: 10, }} maxWidth='xl'>
            <BackdropLoader openBackdrop={openBackdrop} />
            <Card elevation={2}>
                <Typography sx={{ paddingTop: '20px' }} variant='h5' align='center'>People Builder Setup</Typography>
                <CardContent sx={{ overflowY: 'auto', maxHeight: 'calc(95vh - 64px)' }}>
                    <Stack direction="row" alignItems="center" >
                        <Button
                            onClick={() => {
                                setOpenPeopleBuilderForm(true)
                                setPeopleBuilderId(null)
                            }}
                            variant="text"
                            startIcon={<AddIcon />}
                        >
                            Add New
                        </Button>
                    </Stack>
                    <TableContainer sx={{ boxShadow: 3 }} className={globalStyles.tableContainer}>
                        <Table stickyHeader className={globalStyles.tableStyles}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ backgroundColor: '#ccc' }}>Year</TableCell>
                                    <TableCell sx={{ backgroundColor: '#ccc', width: 130 }}>Start Date</TableCell>
                                    <TableCell sx={{ backgroundColor: '#ccc', width: 130 }}>End Date</TableCell>
                                    <TableCell sx={{ backgroundColor: '#ccc', width: 160 }}>Mid-Year Check-In</TableCell>
                                    <TableCell sx={{ backgroundColor: '#ccc', width: 160 }}>End-Year Check-In</TableCell>
                                    <TableCell sx={{ backgroundColor: '#ccc' }}>Status</TableCell>
                                    <TableCell sx={{ backgroundColor: '#ccc' }}>Goals Settings File</TableCell>
                                    <TableCell sx={{ backgroundColor: '#ccc' }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody className={globalStyles.cursor_pointer}>
                                {peopleBuilderSettings.map((pbSettings) => (
                                    <TableRow
                                        hover
                                        key={pbSettings.id}
                                        onMouseEnter={() => setFocusRow(pbSettings.id)}
                                        onMouseLeave={() => setFocusRow(null)}
                                        sx={{ height: '10vh' }}
                                    >
                                        <TableCell>{new Date(pbSettings.start_date).getUTCFullYear()}</TableCell>
                                        <TableCell>{pbSettings.start_date?.split('T')[0]}</TableCell>
                                        <TableCell>{pbSettings.end_date?.split('T')[0]}</TableCell>
                                        <TableCell>{
                                            pbSettings.mid_year_checkin ?
                                                months[moment(pbSettings.mid_year_checkin).month()] :
                                                '-'
                                        }
                                        </TableCell>
                                        <TableCell>{
                                            pbSettings.end_year_checkin ?
                                                months[moment(pbSettings.end_year_checkin).month()] :
                                                '-'
                                        }
                                        </TableCell>
                                        <TableCell>{pbSettings.status ? pbSettings.status.charAt(0).toUpperCase() + pbSettings.status.slice(1) : '-'}</TableCell>
                                        <TableCell>{pbSettings.file_url}</TableCell>
                                        <TableCell>
                                            {(editingRowId !== pbSettings.id) && (
                                                <Stack direction="row" spacing={1}>
                                                    <IconButton
                                                        onClick={() => {
                                                            setPeopleBuilderId(pbSettings.id)
                                                            setOpenPeopleBuilderForm(true)
                                                        }}
                                                        size='small'>
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => {
                                                            setOpenDeletePbSettings(true)
                                                            setPbSettingsSelectedToDelete({
                                                                start_date: pbSettings.start_date?.split('T')[0],
                                                                id: pbSettings.id
                                                            })
                                                        }}
                                                        size='small'>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Stack>
                                            )}
                                            {/* {editingRowId === pbSettings.id && (
                                                <Fade in={editingRowId === pbSettings.id} timeout={500}>
                                                    <Stack direction='row'>
                                                        <Fab size="small">
                                                            <SaveIcon onClick={() => {
                                                                updatePeopleBuilderSettings(pbSettings.id, pbData)
                                                                setEditingRowId(null)
                                                            }} />
                                                        </Fab>
                                                    </Stack>
                                                </Fade>
                                            )} */}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
            <Dialog
                open={openPeopleBuilderForm}
                onClose={handleClose}
                maxWidth='1'
            >
                <DialogContent>
                    <NewPeopleBuilderSettings
                        peopleBuilderSettings={peopleBuilderSettings}
                        setPeopleBuilderSettings={setPeopleBuilderSettings}
                        setPeopleBuilderId={setPeopleBuilderId}
                        peopleBuilderId={peopleBuilderId}
                        setOpenPeopleBuilderForm={setOpenPeopleBuilderForm}
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                open={openDeletePbSettings}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"WARNING!"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        You are about to delete the {pbSettingsSelectedToDelete.year} People Builder. 
                        Are you sure you want to do this?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeletePbSettings(false)}>Cancel</Button>
                    <Button onClick={deletePbSettings} >Confirm</Button>
                </DialogActions>
            </Dialog>
        </Container >
    )
}

export default PeopleBuilderSettings